import { Link } from 'react-router-dom';
import { Search, Bell, ChevronDown, Settings, LogOut, Building, Menu, Shield, Library, BarChart3, ClipboardList, Users, HelpCircle, Brain, Wrench, Home } from 'lucide-react';
import { SearchBox } from '../SearchBox';
import { UserProfile } from '@/types/database';
import { useSidebar } from '@/contexts/SidebarContext';
import { cn } from '@/lib/utils';

interface HeaderProps {
  title: string;
  profile: UserProfile | null;
  userEmail?: string;
  showSearch: boolean;
  setShowSearch: (show: boolean) => void;
  showUserMenu: boolean;
  setShowUserMenu: (show: boolean) => void;
  onSignOut: () => void;
}

export function Header({
  title,
  profile,
  userEmail,
  showSearch,
  setShowSearch,
  showUserMenu,
  setShowUserMenu,
  onSignOut
}: HeaderProps) {
  const { toggleSidebar } = useSidebar();
  const isAdmin = userEmail?.endsWith('@admin.com');

  const getPageIcon = () => {
    const exactTitle = title.split(' ').pop(); // Get the last word in case of "Organization Reports"

    // For admin users
    if (isAdmin) {
      switch (exactTitle) {
        case 'Organizations':
          return <Building className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
        case 'Users':
          return <Users className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      }
    }

    // Match exact page titles
    switch (exactTitle) {
      case 'Dashboard':
        return <Home className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      case 'Library':
        return <Library className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      case 'Reports':
        return <BarChart3 className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      case 'Surveys':
        return <ClipboardList className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      case 'Support':
        return <HelpCircle className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      case 'Settings':
        return <Settings className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      case 'Strategy':
        return <Brain className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      case 'Tools':
        return <Wrench className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
      default:
        return <Home className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" />;
    }
  };

  return (
    <header className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
      <div className="flex items-center min-w-0">
        <button
          onClick={toggleSidebar}
          className="p-2 -ml-2 mr-2 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 lg:hidden"
          aria-label="Toggle menu"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center min-w-0">
          {getPageIcon()}
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white truncate">
            {title}
          </h1>
        </div>
      </div>
      
      <div className="flex items-center space-x-2 sm:space-x-4">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <div className="relative">
            <button
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
              onClick={() => setShowSearch(!showSearch)}
              aria-label="Toggle search"
            >
              <Search className="w-5 h-5" />
            </button>
            <SearchBox isOpen={showSearch} onClose={() => setShowSearch(false)} />
          </div>
          <button 
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
          </button>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-1"
          >
            <div className={cn(
              "h-8 w-8 rounded-full flex items-center justify-center overflow-hidden",
              profile?.avatar_url 
                ? "bg-gray-200 dark:bg-gray-700" 
                : "bg-blue-100 dark:bg-blue-900/50"
            )}>
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              ) : (
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {userEmail?.[0].toUpperCase()}
                </span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400 hidden sm:block" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 border border-gray-200 dark:border-gray-700 z-40">
              <Link
                to="/settings"
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </Link>
              <button
                onClick={onSignOut}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}